import { Chip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import LockIcon from "@mui/icons-material/Lock";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import type { RoleDefinition } from "@xop/dashboard-shared";
import type { JSX } from "react";

/** Shared pill style for the role status chips. */
const statusChipSx = {
  height: 20,
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  "& .MuiChip-label": { px: 1 },
  "& .MuiChip-icon": { ml: 0.75, fontSize: 12 },
} as const;

/**
 * The role card's status pills — an optional "Modified" badge (unsaved edits
 * while collapsed) plus the mutually-exclusive Protected / Customised /
 * Baseline state chip. Extracted from `RolesPanel.tsx`.
 */
export function RoleStatusChips({
  role,
  isExpanded,
  dirty,
}: {
  role: RoleDefinition;
  isExpanded: boolean;
  dirty: boolean;
}): JSX.Element {
  return (
    <>
      {!isExpanded && dirty && (
        <Chip
          label="Modified"
          title="Unsaved permission changes — expand to save"
          sx={[
            statusChipSx,
            {
              border: 1,
              borderColor: (t) => alpha(t.palette.primary.main, 0.4),
              bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
              color: "primary.main",
            },
          ]}
        />
      )}
      {role.protected ? (
        <Chip
          icon={<LockIcon />}
          label="Protected"
          title="Always grants every permission"
          sx={[statusChipSx, { border: 1, borderColor: "divider", bgcolor: "surfaceSoft", color: "text.disabled" }]}
        />
      ) : role.customized ? (
        <Chip
          icon={<VerifiedUserIcon />}
          label="Customised"
          sx={[
            statusChipSx,
            {
              border: 1,
              borderColor: (t) => alpha(t.palette.primary.main, 0.4),
              bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
              color: "primary.main",
            },
          ]}
        />
      ) : (
        <Chip
          label="Baseline"
          sx={[statusChipSx, { border: 1, borderColor: "divider", bgcolor: "background.default", color: "text.disabled" }]}
        />
      )}
    </>
  );
}
